import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { BranchSelect } from '../dimensions';
import { SEARCH_BG, type Branch } from './pc-helpers';

export interface PCOverviewProps {
  branches: readonly Branch[];
  companyId: string | undefined;
  filterBranchId: string;
  setFilterBranchId: (id: string) => void;
  statusF: string;
  setStatusF: (s: string) => void;
  q: string;
  setQ: (q: string) => void;
  isPending: boolean;
  error: Error | null;
  onPick: (b: Branch) => void;
}

export function PCOverview({
  branches,
  companyId,
  filterBranchId,
  setFilterBranchId,
  statusF,
  setStatusF,
  q,
  setQ,
  isPending,
  error,
  onPick,
}: PCOverviewProps) {
  return (
    <>
      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา รหัสสาขา / ชื่อสาขา"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 32,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '10px center',
            }}
          />
          <BranchSelect companyId={companyId} value={filterBranchId} onChange={setFilterBranchId} />
          <select className="select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            <option value="all">สถานะ: ทั้งหมด</option>
            <option value="submitted">ส่งรายงานแล้ว</option>
            <option value="pending">รออนุมัติ</option>
            <option value="approved">อนุมัติแล้ว</option>
            <option value="reimbursed">ชดเชยเงินแล้ว</option>
            <option value="overdue">เกินกำหนด</option>
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="meta">แสดง {branches.length} สาขา</span>
            <button className="btn sm" disabled title="เร็วๆ นี้">
              <Icon name="filter" size={12} /> ตัวกรอง
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 560 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input type="checkbox" />
                </th>
                <th style={{ width: 90 }}>รหัสสาขา</th>
                <th>ชื่อสาขา</th>
                <th>ผู้รับผิดชอบ</th>
                <th className="num">วงเงิน (Float)</th>
                <th className="num">ใช้ไป</th>
                <th style={{ width: 160 }}>การใช้วงเงิน</th>
                <th className="num">รายการเดือนนี้</th>
                <th className="num">ยอดเบิกรวม</th>
                <th>ส่งล่าสุด</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td colSpan={11} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={11}
                    className="muted"
                    style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {error.message}
                  </td>
                </tr>
              ) : branches.length === 0 ? (
                <tr>
                  <td colSpan={11} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่มีข้อมูล — เปิด float สาขาแรกเพื่อเริ่มบันทึกเงินสดย่อย
                  </td>
                </tr>
              ) : null}
              {branches.slice(0, 80).map((b) => (
                <tr key={b.id} onClick={() => onPick(b)} style={{ cursor: 'pointer' }}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" />
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{b.id}</a>
                  </td>
                  <td>{b.name}</td>
                  <td className="muted">{b.custodian}</td>
                  <td className="num tnum">{fmt(b.float)}</td>
                  <td className="num tnum">{fmt(b.used)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${b.usedPct}%`,
                            height: '100%',
                            background:
                              b.usedPct >= 90
                                ? 'var(--negative)'
                                : b.usedPct >= 70
                                  ? 'var(--warning)'
                                  : 'var(--positive)',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 14,
                          color: 'var(--text-muted)',
                          minWidth: 32,
                          textAlign: 'right',
                        }}
                      >
                        {b.usedPct}%
                      </span>
                    </div>
                  </td>
                  <td className="num">{b.entries}</td>
                  <td className="num tnum">
                    <b>{fmt(b.total)}</b>
                  </td>
                  <td>{fmtDate(b.lastSub)}</td>
                  <td>
                    {b.status === 'submitted' ? (
                      <span className="badge info">
                        <span className="bdot" />
                        ส่งแล้ว
                      </span>
                    ) : b.status === 'pending' ? (
                      <span className="badge warning">
                        <span className="bdot" />
                        รออนุมัติ
                      </span>
                    ) : b.status === 'approved' ? (
                      <span className="badge positive">
                        <span className="bdot" />
                        อนุมัติ
                      </span>
                    ) : b.status === 'reimbursed' ? (
                      <span className="badge positive">
                        <span className="bdot" />
                        ชดเชยแล้ว
                      </span>
                    ) : (
                      <span className="badge negative">
                        <span className="bdot" />
                        เกินกำหนด
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>
            {branches.length > 0
              ? `หน้า 1 จาก ${Math.ceil(branches.length / 80)} · แสดง 1-${Math.min(
                  80,
                  branches.length,
                )} จาก ${branches.length} สาขา`
              : 'ยังไม่มีรายการ'}
          </span>
        </div>
      </div>
    </>
  );
}
